import { z } from 'zod';
import { barcodeKindSchema } from './enums.js';
export const barcodeInputSchema = z.object({
    /** Raw scan, may contain separators and the add-on. */
    code: z.string().trim().min(1).max(40),
    /** Add-on if the scanner reported it separately. */
    addon: z.string().trim().max(5).optional(),
});
const digitsOnly = (value) => value.replace(/\D/g, '');
/** Modulo-10 check digit used by EAN-13, UPC-A and ISBN-13. */
export function gtinCheckDigit(digitsWithoutCheck) {
    let sum = 0;
    // Weights alternate 3/1 from the right, so start from the end.
    for (let index = digitsWithoutCheck.length - 1, step = 0; index >= 0; index -= 1, step += 1) {
        const digit = Number(digitsWithoutCheck[index]);
        sum += step % 2 === 0 ? digit * 3 : digit;
    }
    return (10 - (sum % 10)) % 10;
}
export function isValidGtin(digits) {
    if (!/^\d{8}$|^\d{12,14}$/.test(digits))
        return false;
    const body = digits.slice(0, -1);
    const check = Number(digits.slice(-1));
    return gtinCheckDigit(body) === check;
}
/** ISBN-10 check digit is modulo 11 with an X for 10. */
export function isValidIsbn10(value) {
    const cleaned = value.replace(/[\s-]/g, '').toUpperCase();
    if (!/^\d{9}[\dX]$/.test(cleaned))
        return false;
    let sum = 0;
    for (let index = 0; index < 9; index += 1) {
        sum += Number(cleaned[index]) * (10 - index);
    }
    const check = cleaned[9] === 'X' ? 10 : Number(cleaned[9]);
    return (sum + check) % 11 === 0;
}
export function isbn10To13(value) {
    const cleaned = value.replace(/[\s-]/g, '').toUpperCase();
    if (!isValidIsbn10(cleaned))
        return null;
    const body = `978${cleaned.slice(0, 9)}`;
    return `${body}${gtinCheckDigit(body)}`;
}
/** UPC-A is an EAN-13 with a leading zero. */
export function upcToEan13(upc12) {
    return `0${upc12}`;
}
/**
 * Splits a raw scan into code and add-on and classifies it.
 *
 * US floppies carry a 12 digit UPC-A plus a 5 digit EAN-5 supplemental that
 * encodes issue number, cover variant and printing; without it only the series
 * is identifiable. German newsstand issues use a press EAN whose supplemental
 * carries the issue number. Books (paperbacks, albums, manga) use an EAN-13
 * starting with 978/979, which is an ISBN.
 */
export function normalizeBarcode(input) {
    const rawDigits = digitsOnly(input.code);
    const explicitAddon = input.addon ? digitsOnly(input.addon) : '';
    let digits = rawDigits;
    let addon = explicitAddon;
    // A scanner that reports everything in one string: 12 or 13 digits of code
    // followed by a 2 or 5 digit supplemental.
    if (!addon) {
        for (const codeLength of [13, 12]) {
            for (const addonLength of [5, 2]) {
                if (digits.length === codeLength + addonLength) {
                    digits = rawDigits.slice(0, codeLength);
                    addon = rawDigits.slice(codeLength);
                }
            }
        }
    }
    const checkDigitValid = isValidGtin(digits);
    let kind = 'unknown';
    let isbn13 = null;
    let upc12 = null;
    if (digits.length === 12) {
        kind = 'upc_a';
        upc12 = digits;
    }
    else if (digits.length === 13) {
        if (/^97[89]/.test(digits)) {
            kind = 'isbn_13';
            isbn13 = digits;
        }
        else if (/^0{1}/.test(digits)) {
            // EAN-13 with a leading zero is a UPC-A in disguise.
            kind = 'upc_a';
            upc12 = digits.slice(1);
        }
        else if (/^4[0-9]/.test(digits) && addon.length > 0) {
            // German press codes start with the country prefix and always carry a
            // supplemental that holds the issue number.
            kind = 'press_ean';
        }
        else {
            kind = 'ean_13';
        }
    }
    else if (digits.length === 10) {
        const converted = isbn10To13(digits);
        if (converted) {
            kind = 'isbn_13';
            isbn13 = converted;
            digits = converted;
        }
    }
    return {
        normalized: digits,
        kind,
        addon: addon || null,
        isbn13,
        upc12,
        checkDigitValid: kind === 'isbn_13' && isbn13 !== null ? isValidGtin(isbn13) : checkDigitValid,
    };
}
export const normalizedBarcodeSchema = z.object({
    normalized: z.string(),
    kind: barcodeKindSchema,
    addon: z.string().nullable(),
    isbn13: z.string().nullable(),
    upc12: z.string().nullable(),
    checkDigitValid: z.boolean(),
});
//# sourceMappingURL=barcode.js.map